import range from './range'

const MAX_LENGTH = 15
const INITIAL_LENGTH = 2
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz-'

/**
 *
 * @param {Object} options - Options
 * @param
 * @returns
 */
export function IDGenerator({
  maxIDLength,
  initialIDLength,
  alphabet,
}: {
  maxIDLength: number
  initialIDLength: number
  alphabet: string
}): () => string {
  let currentLength = initialIDLength

  // eslint-disable-next-line prefer-const
  let filledCharacters = [
    ...(Array(maxIDLength - currentLength).fill(-1) as number[]),
    ...(Array(currentLength).fill(0) as number[]),
  ]

  let availableIDs = range(initialIDLength, maxIDLength + 1).reduce(
    // using arrangement with repetition formula
    (acc: number, n: number) => acc + Math.pow(alphabet.length, n),
    0
  )

  return function getID() {
    if (availableIDs == 0) {
      throw new Error(`No more IDs available with the provided alphabet and max length`)
    }

    availableIDs--
    const newID = filledCharacters.slice(-1 * currentLength).map((at: number) => alphabet[at])

    /**
     * [0, 0, 0, 0]
     *              ^ at
     */
    let at = filledCharacters.length

    /**
     * move to the left in the `filledCharacters` array if
     * we have surpassed the last possible character in `alphabet`.
     * That means:
     * [0,0,0,(alphabet.length - 1)]
     * Becomes:
     * [0,0,1,0]
     */

    do {
      at--
      filledCharacters[at] = (filledCharacters[at] + 1) % alphabet.length
    } while (filledCharacters[at] == 0 && at >= filledCharacters.length - currentLength)

    /**
     * if we moved to the left as much as `currentLength` allowed,
     * and we have incremented as many times as we could, for each
     * position so we're back to `0`, due to the module operation,
     * i.e., if `currentLength = 3`, for example:
     *
     * [0, 0, 0, 0]
     *     ^ currentLength
     *  ^at
     *
     * So we need to increase `currentLength` to start incrementing from there:
     * [0, 0, 0, 0]
     *  ^ currentLength
     */
    if (filledCharacters.length - at > currentLength) {
      currentLength++
    }

    return `genid-${newID.join('')}`
  }
}

// this configuration is capable of generating 469172025408063600 different IDs.
const getID = IDGenerator({
  maxIDLength: MAX_LENGTH,
  alphabet: ALPHABET,
  initialIDLength: INITIAL_LENGTH,
})

export default getID
